import React, { useCallback, useState, lazy } from 'react';
import styled from 'styled-components';

import { Field } from 'react-final-form';
import {
  Box as MuiBox,
  Chip as MuiChip,
  Popover as MuiPopover,
  useTheme,
} from '@mui/material';
import { JamIcon } from 'components/ui';
import { useTranslation } from 'react-i18next';

const FormPopoverSelect = ({ name, options, onChange, disabled = false, style = {}, placeholder, 'data-testid': dataTestId }) => {
  const { t } = useTranslation();
  const theme = useTheme()

  const [anchorEl, setAnchorEl] = useState(null);
  const [touched, setTouched] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setTouched(true);
    setAnchorEl(null);
  };

  const onPick = useCallback(
    (input, value) => {
      input.onChange(onChange ? onChange(value) : value);
      handleClose();
    },
    [handleClose]
  );

  const open = Boolean(anchorEl);

  const id = open ? 'simple-popover' : undefined;

  return (
    <Field name={name}>
      {({ input, meta }) => {
        return (
          <>
            <MuiBox style={style}>
              <MuiBox display="flex" alignItems="center">
                <MuiBox mr={1}>
                  <SMuiChip
                    label={
                      input.value?.label
                        ? input.value.label
                        : options.find((item) => item.value === input.value)
                            ?.label || (placeholder ? t(placeholder) : '')
                    }
                    isPlaceholder={!input.value && !options.find((item) => item.value === input.value)}
                    onClick={(e) => {
                      if (!disabled) handleClick(e);
                    }}
                    clickable={!disabled}
                    data-testid={dataTestId}
                  />
                </MuiBox>
                <MuiBox
                  style={{
                    cursor: !disabled ? 'pointer' : 'auto',
                    display: 'flex',
                    opacity: !disabled ? 1 : 0,
                  }}
                  onClick={(e) => {
                    if (!disabled) handleClick(e);
                  }}
                >
                  <JamIcon
                    icon="chevron-down"
                    size={18}
                    color={theme.palette.colors.black100}
                  />
                </MuiBox>

                <MuiPopover
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                >
                  <MuiBox p={2}>
                    {options.map((item, idx) => (
                      <MuiBox
                        style={{ cursor: 'pointer' }}
                        checked={item.value === input.value}
                        key={idx}
                        onClick={() => onPick(input, item)}
                      >
                        {item.label}
                      </MuiBox>
                    ))}
                  </MuiBox>
                </MuiPopover>
              </MuiBox>

              {meta.error && (meta.touched || touched) && (
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 400,
                    marginTop: '5px',
                    color: theme.palette.colors.red100,
                  }}
                >
                  {t(meta.error[0])}
                </div>
              )}
            </MuiBox>
          </>
        );
      }}
    </Field>
  );
};

const SMuiChip = styled(MuiChip)`
  border-radius: 5px;
  background: ${({theme}) => theme.palette.colors.gray100};
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: ${({theme, isPlaceholder}) => 
    isPlaceholder ? theme.palette.colors.gray400 : theme.palette.colors.black100};
  font-style: ${({isPlaceholder}) => isPlaceholder ? 'italic' : 'normal'};
  cursor: ${({clickable}) => clickable ? 'pointer' : 'default'};
  
  &:hover {
    background: ${({theme, clickable}) => 
      clickable ? theme.palette.colors.gray200 : theme.palette.colors.gray100};
  }
`;

export default FormPopoverSelect;
